import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Menu } from 'lucide-react';
import { RacequbeLogo } from './RacequbeLogo';

type View =
  | 'login'
  | 'register'
  | 'leagues'
  | 'standings'
  | 'createLeague'
  | 'joinLeague'
  | 'account'
  | 'admin';

interface NavigationProps {
  userEmail: string;
  displayName?: string;
  onLogout: () => void;
  currentView: View;
  onNavigate: (view: View) => void;
  isAdmin?: boolean;
  contextTitle?: string;
}

interface NavItem {
  view: View;
  label: string;
  showOnDesktop?: boolean;
  showOnMobile?: boolean;
  adminOnly?: boolean;
}

export const Navigation: React.FC<NavigationProps> = ({
  userEmail,
  displayName,
  onLogout,
  currentView,
  onNavigate,
  isAdmin = false,
  contextTitle,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Shared navigation items configuration
  const navItems: NavItem[] = useMemo(() => [
    { view: 'admin', label: 'Admin', showOnDesktop: true, showOnMobile: true, adminOnly: true },
    { view: 'leagues', label: 'Leagues', showOnDesktop: true, showOnMobile: true },
  ], []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavigate = (view: View) => {
    onNavigate(view);
    setMobileMenuOpen(false); // Close menu after navigation
  };

  // Filter nav items based on admin status and display context
  const getVisibleNavItems = (isMobile: boolean) => {
    return navItems.filter(item => {
      if (item.adminOnly && !isAdmin) return false;
      return isMobile ? item.showOnMobile !== false : item.showOnDesktop !== false;
    });
  };

  return (
    <nav className="sticky top-0 z-50 bg-black/95 backdrop-blur-sm border-b-2 border-transparent" style={{ borderImage: 'linear-gradient(to right, hsl(var(--brand-primary)), hsl(var(--brand-secondary))) 1' }}>
      <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-4">
        <button
          onClick={() => handleNavigate('leagues')}
          className="flex items-baseline gap-1 flex-shrink-0 transition-opacity duration-200 hover:opacity-80"
        >
          <RacequbeLogo className="h-8 w-auto" />
        </button>

        {/* Context title — centered between logo and nav items */}
        <div className="flex-1 flex justify-center overflow-hidden">
          {contextTitle && (
            <span className="text-sm font-semibold truncate max-w-[200px]">
              {contextTitle}
            </span>
          )}
        </div>

        {/* Hamburger menu button - visible only on mobile */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleMobileMenu}
          className="md:hidden h-10 w-10 flex-shrink-0 transition-all duration-200"
          aria-label="Toggle navigation menu"
          aria-expanded={mobileMenuOpen}
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Desktop navigation - visible on >= 768px */}
        <div className="hidden md:flex items-center gap-2">
          {getVisibleNavItems(false).map(item => (
            <Button
              key={item.view}
              variant="ghost"
              onClick={() => handleNavigate(item.view)}
              className={`h-10 transition-all duration-200 ${item.adminOnly ? 'text-primary font-semibold' : ''}`}
            >
              {item.label}
            </Button>
          ))}
          <Button
            variant="ghost"
            onClick={() => handleNavigate('account')}
            className="h-10 transition-all duration-200"
          >
            {displayName || userEmail}
          </Button>
          <Button
            variant="ghost"
            onClick={onLogout}
            className="h-10 transition-all duration-200"
          >
            Logout
          </Button>
        </div>

        {/* Mobile navigation menu - visible only when open on < 768px */}
        {mobileMenuOpen && (
          <div className="fixed top-[65px] left-0 right-0 bg-background border-b border-border flex flex-col p-4 gap-2 z-10 md:hidden max-h-[calc(100vh-65px)] overflow-y-auto">
            {getVisibleNavItems(true).map(item => (
              <Button
                key={item.view}
                variant={currentView === item.view ? 'default' : 'ghost'}
                onClick={() => handleNavigate(item.view)}
                className={`h-12 w-full justify-start rounded transition-all duration-200 ${item.adminOnly ? 'text-primary font-semibold' : ''}`}
              >
                {item.label}
              </Button>
            ))}
            <Button
              variant={currentView === 'account' ? 'default' : 'ghost'}
              onClick={() => handleNavigate('account')}
              className="h-12 w-full justify-start rounded transition-all duration-200"
            >
              Account
            </Button>
            <div className="py-3 px-4 border-t border-border mt-2">
              <span className="text-sm text-muted-foreground">{displayName || userEmail}</span>
            </div>
            <Button
              variant="outline"
              onClick={onLogout}
              className="h-12 w-full rounded transition-all duration-200"
            >
              Logout
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
};
